import React, { useCallback, useEffect, useState } from "react";
import { connect, useSelector } from "react-redux";

import { addLayout } from "store/thunk/layout";
import useAppInsightsHandler from "hooks/useAppInsightsHandler.js";

import { getAllUsers, getUsersLayout, getUsersSystem } from "api/layout";

import getLogger from "utils/logger.util";
import { capitalizeFirstLetter, customDebounce } from "utils/utils";
import { DROPDOWN_STRINGS, InputErrorStrings, LAYOUT_STRINGS, valueStrings, WidgetStrings } from "constants/constantStrings";
import { INPUT_MAXIMUM, INPUT_MINIMUM } from "constants/globalConstants";

import ChartNormalizedTagDialog from "pages/Chart/ChartHeader/ChartNormalizedTagDialog.jsx";
import AutogenerateForm from "pages/Report/AutogenerateForm";
import CustomModal from "components/CustomModal";
import Dropdown from "components/Dropdown";
import InputBox from "components/InputBox";

import "./LayoutAddForm.scss";

function LayoutAddForm(props) {
  const [isHome, setIsHome] = useState(false);
  const [layoutName, setLayoutName] = useState();
  const [isFormInvalid, setIsFormInvalid] = useState(true);
  const [systemsUsers, setSystemsUsers] = useState([]);
  const [user, setUser] = useState({});
  const [copyUser, setCopyUser] = useState({});
  const [userError, setUserError] = useState(null);
  const [copySystem, setCopySystem] = useState({});
  const [copyLayout, setCopyLayout] = useState({});
  const [layoutNonUniqueErr, setLayoutNonUniqueErr] = useState(null);
  const [isAutoGenerateEnabled, setIsAutoGenerateEnabled] = useState(false);
  const [showNormalizationDialog, setShowNormalizationDialog] = useState(false);
  const page = capitalizeFirstLetter(props.page.slice(0, -1));

  const ssoUser = useSelector(state => state.user);
  const objAppInsights = useAppInsightsHandler();
  const Logger = getLogger("LayoutAddForm");

  useEffect(() => {
    const fetchUser = async () => {
      const users = await getAllUsers();
      const defaultUser = users.find(user => user.Email === ssoUser.UserEmail);
      setUser(defaultUser);
      const sortedUsers = users.sort((a, b) => a.Email.localeCompare(b.Email));
      setSystemsUsers(sortedUsers);
    };
    if (props.admin) fetchUser();
  }, []);

  const handleCloseAddLayoutForm = () => {
    props.setIsAddLayoutFormOpen(false);
    setLayoutName("");
    setUser({});
    setIsHome(false);
    setCopyUser({});
    setCopySystem({});
    setCopyLayout({});
  };

  const handleChange = () => setIsHome(!isHome);

  const handleUserChange = value => {
    setUser(value);
    if (!value) {
      setUserError(InputErrorStrings.required);
      setIsFormInvalid(true);
    } else {
      setUserError(null);
      setIsFormInvalid(!layoutName);
    }
  };
  const onLayoutNameChange = value => {
    setLayoutName(value);
    if (!value || value.trim() === "") {
      setIsFormInvalid(true);
      setLayoutNonUniqueErr(InputErrorStrings.required);
    } else if (value.trim().length < INPUT_MINIMUM || value.trim().length > INPUT_MAXIMUM) {
      setIsFormInvalid(true);
      setLayoutNonUniqueErr(LAYOUT_STRINGS.titleLength);
    } else {
      setIsFormInvalid(!user);
      debounceFn(value);
      setLayoutNonUniqueErr("");
    }
  };

  const isUniqueLayoutName = name => {
    const { layouts } = props;
    const isNotUnique = layouts.some(layout => layout?.Name?.toLowerCase() === name?.toLowerCase());
    const errMsg = isNotUnique ? LAYOUT_STRINGS.nonUniqueLayoutName : "";
    setLayoutNonUniqueErr(errMsg);
    setIsFormInvalid(!name || !user || isNotUnique);
  };

  const checkLayoutUniqueNameAdmin = () => {
    let isNotUnique = false;
    if (layoutName.trim() !== "" && Object.keys(user).length !== 0 && props.admin && props.layouts) {
      const userData = props.layouts.filter(userObject => userObject.UserID === user.UserId);
      if (Object.keys(user).length !== 0 && userData.length > 0) {
        const userSystemLayouts = userData.filter(system => system.SystemID === props.systemID);
        if (userSystemLayouts.length) {
          isNotUnique = userSystemLayouts.some(layout => layout.Name.toLowerCase() === layoutName.toLowerCase());
          const errMsg = isNotUnique ? LAYOUT_STRINGS.nonUniqueLayoutName : "";
          setLayoutNonUniqueErr(errMsg);
        }
      }
    }
    return isNotUnique;
  };
  const debounceFn = useCallback(customDebounce(isUniqueLayoutName), []);
  const handleCopyUserChange = async value => {
    try {
      setCopySystem({});
      setCopyLayout({});
      if (value) {
        const response = await getUsersSystem(props.page, value);
        if (response && response.Systems.length > 0) value.Systems = response.Systems;
      }
      value = value || {};
      setCopyUser(value);
    } catch (error) {
      Logger.error("Error in copy layout ", error);
    }
  };

  const handleCopySystemChange = async value => {
    try {
      setCopyLayout({});
      if (value) {
        let filteredLayouts;
        const response = await getUsersLayout(props.page, value?.SysId, copyUser);
        if (response && response.Layouts.length > 0) {
        if (props.page && props.page.toLowerCase() === "charts") {
           filteredLayouts = response.Layouts.map(item => {
            if (item.NormalizedTag) {
              return { ...item, Name: item.Name + " (N)" };
            }
            return item;
          });
        } else {
          filteredLayouts = response.Layouts.filter(item => !item.NormalizedTag);
        }
      }
        value.Layouts = filteredLayouts;
      }
      value = value || {};
      setCopySystem(value);
    } catch (error) {
      Logger.error(error);
    }
  };

  const handleCopyLayoutChange = value => setCopyLayout(value);

  const handleSubmit = () => {
    objAppInsights.trackEvent("addLayoutFormSubmitted");
    if (!checkLayoutUniqueNameAdmin()) {
      const copyLayoutID = copyLayout?.Id || null;
      const scheduling = null;
      const hasNormalizedTag = copyLayout?.NormalizedTag;
      if (props.page && props.page.toLowerCase() === "charts" && hasNormalizedTag) {
        setShowNormalizationDialog(true);
      } else {
        props.onSubmitNewLayoutAdmin(props.page, layoutName.trim(), isHome, user.UserId, copyLayoutID, scheduling, isAutoGenerateEnabled);
        setUser({});
        setIsHome(false);
        setLayoutName("");
        setCopyUser({});
        setCopySystem({});
        setCopyLayout({});
        props.setIsAddLayoutFormOpen(false);
      }
    }
  };

  const handleCloseBaseline = async baseline => {
    setShowNormalizationDialog(false);
    if (!baseline) {
      return;
    }
    if (props.page && props.page.toLowerCase() === "charts") {
      const copyLayoutID = copyLayout?.Id || null;
      const scheduling = null;
      await props.onSubmitNewLayoutAdmin(
        props.page,
        layoutName.trim(),
        isHome,
        user.UserId,
        copyLayoutID,
        scheduling,
        isAutoGenerateEnabled,
        baseline.Baseline
      );
      setUser({});
      setIsHome(false);
      setLayoutName("");
      setCopyUser({});
      setCopySystem({});
      setCopyLayout({});
      props.setIsAddLayoutFormOpen(false);
    }
  };

  const getDescription = () => LAYOUT_STRINGS.addLayoutDesc.replace("[%]", page.toLowerCase());
  const getTitle = () => LAYOUT_STRINGS.addLayoutModalTitle.replace("[%]", page);

  return (
    <div className="dashAddLayoutBtn">
      <CustomModal
        show={props.isAddLayoutFormOpen}
        title={getTitle()}
        description={getDescription()}
        secondaryDescription={valueStrings.mandatoryField}
        confirmBtnText={valueStrings.save}
        close={handleCloseAddLayoutForm}
        confirmBtnAction={handleSubmit}
        confirmDisabled={isFormInvalid || !layoutName}
        backBtnTooltip={props?.backBtnTooltip}
      >
        <div className="AddLayoutContainerPadding">
          {props.admin && (
            <div className="form-group">
              <Dropdown
                labelText={`${LAYOUT_STRINGS.user}*`}
                value={user}
                options={systemsUsers}
                errorMsg={userError}
                placeholder={DROPDOWN_STRINGS.chooseOptionStr}
                getOptionLabel={option => option?.Email}
                onChange={handleUserChange}
              />
            </div>
          )}
          <div className="form-group">
            <label className="bodyMainHeadingText captilise__first-letter" htmlFor="widgetTitle">
              {LAYOUT_STRINGS.layoutTitle.replace("[%]", page)}*
            </label>
            <InputBox
              name="layoutName"
              value={layoutName}
              placeholder={WidgetStrings.titlePlaceholder}
              requiredMsg={InputErrorStrings.required}
              errorMsg={layoutNonUniqueErr}
              onChange={onLayoutNameChange}
              maxLength={INPUT_MAXIMUM}
            />
          </div>
        </div>
        {props.setIsReportRerendering && (
          <div className="AddLayoutContainerPadding">
            {/* <AutogenerateForm autogenerate={autogenerate} setAutogenerate={setAutogenerate} setIsAutoGenerateEnable={setIsAutoGenerateEnabled}/> */}
            <AutogenerateForm isAutoGenerateEnabled={isAutoGenerateEnabled} setIsAutoGenerateEnabled={setIsAutoGenerateEnabled} />
          </div>
        )}

        <div className={`${isHome ? "isHomeIcon" : "setHomeIcon"} iconAlignment pointer`} onClick={handleChange}>
          <span className="add-layout-list-item"> {LAYOUT_STRINGS.setAsPrimary.replace("[%]", page)} </span>
        </div>

        {props.admin && (
          <div className="AddLayoutContainerPadding">
            <>
              <label className="smalldescriptionTealText fullWidth">{LAYOUT_STRINGS.copyLayout.replace("[%]", page)}</label>
              <div className="form-group">
                <Dropdown
                  labelText={LAYOUT_STRINGS.user}
                  value={copyUser}
                  placeholder={DROPDOWN_STRINGS.chooseOptionStr}
                  options={systemsUsers}
                  getOptionLabel={option => option?.Email}
                  onChange={handleCopyUserChange}
                />
              </div>
              <div className="form-group">
                <Dropdown
                  labelText={LAYOUT_STRINGS.system}
                  disabled={Object.keys(copyUser).length === 0}
                  value={copySystem}
                  placeholder={DROPDOWN_STRINGS.chooseOptionStr}
                  options={
                    Object.keys(copyUser).length === 0 ? [] : copyUser?.Systems?.sort((a, b) => a.SystemName.localeCompare(b.SystemName)) || []
                  }
                  getOptionLabel={option => option.SystemName}
                  onChange={handleCopySystemChange}
                />
              </div>

              <div className="form-group">
                <Dropdown
                  labelText={`${LAYOUT_STRINGS.layoutName.replace("[%]", page)}`}
                  disabled={Object.keys(copyUser).length === 0 || Object.keys(copySystem).length === 0}
                  value={copyLayout}
                  placeholder={DROPDOWN_STRINGS.chooseOptionStr}
                  options={Object.keys(copySystem).length === 0 ? [] : copySystem?.Layouts?.sort((a, b) => a.Name.localeCompare(b.Name)) || []}
                  getOptionLabel={option => option.Name}
                  onChange={handleCopyLayoutChange}
                />
              </div>
              <div className="descriptionText">
                {props.page.toLowerCase() === "charts" ? LAYOUT_STRINGS.copyNormChartInfo : LAYOUT_STRINGS.copyLayoutInfo}
              </div>
            </>
          </div>
        )}
      </CustomModal>
      {showNormalizationDialog && <ChartNormalizedTagDialog isEdit isNormChartCopy handleClose={handleCloseBaseline} />}
    </div>
  );
}

const getLayouts = (layouts, ownProps) => {
  let result = [];
  const { page, systemID } = ownProps;
  const systemsLayout = layouts[systemID];
  if (systemsLayout) {
    result = systemsLayout[page] || [];
  }
  return result;
};

const mapStateToProps = ({ layout, user }, ownProps) => ({
  admin: user.isAdmin,
  layouts: getLayouts(layout.layouts, ownProps),
  currentLayout: getLayouts(layout.currentLayouts, ownProps)
});

const mapDispatchToProps = dispatch => ({
  onSubmitNewLayout: (page, layoutName, isHome) => dispatch(addLayout(page, layoutName, isHome)),
  onSubmitNewLayoutAdmin: (page, layoutName, isHome, user, copyLayout, scheduling, AutoReport, baseline) =>
    dispatch(addLayout(page, layoutName, isHome, user, copyLayout, scheduling, AutoReport, baseline))
});

export default connect(mapStateToProps, mapDispatchToProps)(LayoutAddForm);
